import ReactDOM from 'react-dom';
import React, { useRef, useEffect } from 'react';
import { useI18n } from '@oneaudi/i18n-context';
import {
  Text,
  SelectField,
  InfoBox,
  ToggleSwitch,
  InfoBoxProps,
  SearchField,
  SearchFieldProps,
} from '@oneaudi/unified-web-components';
import { OneCMSContent } from '../../../types/oneCMS';
import {
  initializeAutocomplete,
  useServicesContext,
  setAddressFromLatLngGeocoder,
  getScsUrlPartsFromConfiguration,
  FilterContext,
  initializeInitialStateFromLocalStorage,
  updateLocationSearchInputLocalStorage,
  FilterDTO,
  useApplyFilters,
  useClearAllDealerFilters,
  shouldBeUsedFilterDataWithoutClearingDealers,
  FilterContextValue,
  setLocationFilterToDefaultState,
  trackGiveConsent,
  trackRemoveConsent,
  DynamicComponent,
  isLegacy,
  dynamicStylingHelper,
  dynamicStyling,
  useGoogleMaps,
  useMandatoryAreaSearchContext,
} from '@oneaudi/vtp-shared';
import {
  SearchWrapper,
  LocateMeButton,
  QuickLocationFilterWrapper,
} from '../styles/QuickLocationFilter.styles';
import { ExpiringStorage } from '@oneaudi/stck-store';
import {
  SYS_COLOR_SURFACE_NEUTRAL_700_DARK,
  SYS_SPACE_FIXED_150,
  SYS_SPACE_FIXED_200,
  SYS_SPACE_FIXED_300,
} from '@oneaudi/unified-web-common';
import { SubHeadText } from '../styles/SwitchNCUC.styles';
import styled, { createGlobalStyle } from 'styled-components';
import { ConsentModal, saveConsentToStorage } from '@oneaudi/two-click';
import { LayerHandleV1 } from '@volkswagen-onehub/gfa-layer-manager';
import { FocusLayerSizeV2 } from '@volkswagen-onehub/layer-manager';
import {
  SYS_COLOR_TEXT_COPY_DEFAULT_DARK,
  SYS_GRADIENT_ELEVATION_LEVEL3_FILL_DARK,
} from '@oneaudi/unified-web-alpha-common';
import { FeatureServiceDependencies } from '../../../FeatureHubAppDefinition';

export interface QuickLocationFilterProps {}

export const QuickLocationFilter: React.FC<QuickLocationFilterProps> = () => {
  const { getFeatureServices, getConfiguration, getEnvironmentConfig } = useServicesContext();
  const {
    'layer-manager': layerManager,
    'audi-content-service': audiContentService,
    'audi-tracking-service': trackingService,
    'gfa:locale-service': localeService,
  } = getFeatureServices<FeatureServiceDependencies>();
  const { mileageUnit, locationFilterConfig_radiusOptions, locationFilterConfig_defaultRadius } =
    audiContentService.getContent().fields as OneCMSContent;

  const scsUrlParts = getScsUrlPartsFromConfiguration(getConfiguration()!, getEnvironmentConfig()!);
  const country = localeService.countryCode;
  const { enableMandatoryAreaSearch, openMandatoryAreaSearchModal } =
    useMandatoryAreaSearchContext();

  const localStorageLocationSearchInputKey = enableMandatoryAreaSearch
    ? `VTP_${scsUrlParts?.marketPath}_mandatoryLocationSearch--input`
    : `VTP_${scsUrlParts?.marketPath}_locationSearch--input`;
  const radiusOptions = locationFilterConfig_radiusOptions || [10, 20, 50, 100, 200];
  const defaultRadius = locationFilterConfig_defaultRadius || 10;

  const locationSearchHeadlineLabel = useI18n({
    id: `nemo.sc.plp.quicklocationfilter.locationSearchInputLabel`,
    defaultMessage: 'Location',
  });
  const locationSearchDefaultInputLabel = useI18n({
    id: `nemo.sc.plp.quickLocationFilter.locationSearchDefaultInputLabel`,
    defaultMessage: '',
  });
  const locationSearchInputLabel = useI18n({
    id: `nemo.sc.plp.quicklocationfilter.locationSearchDescriptionLabel`,
    defaultMessage: 'Enter city or postcode',
  });
  const distanceRadiusLabel = useI18n({
    id: `nemo.sc.plp.electrofiltration.distanceRadiusLabel`,
    defaultMessage: 'Distance',
  });

  const clearLocationSearchInputButtonAriaLabel = useI18n({
    id: `nemo.sc.plp.locationfilter.clearLocationSearchInputButtonAriaLabel`,
    defaultMessage: 'Clear the text in the location search input field.',
  });
  const locateMeButtonAriaLabel = useI18n({
    id: `nemo.sc.plp.locationfilter.cookieConsentRequestMap.locateMeButtonAriaLabel`,
    defaultMessage: 'Use your location.',
  });
  const consentModalHeadline = useI18n({
    id: `google-maps-two-click-consent.headline`,
    defaultMessage: '',
  });
  const consentModalBody = useI18n({
    id: `google-maps-two-click-consent.copyText`,
    defaultMessage: '',
  });
  const consentModalToggle = useI18n({
    id: `google-maps-two-click-consent.toggleLabel`,
    defaultMessage: '',
  });
  const toggleText = useI18n({
    id: `nemo.sc.filter.locationfilter.cookieConsentControl.Toggle`,
    defaultMessage: '',
  });
  const grantConsentBannerHeadline = useI18n({
    id: `nemo.sc.plp.quickLocationFilter.cookieConsentHeadline`,
    defaultMessage: '',
  });
  const grantConsentBannerText = useI18n({
    id: `nemo.sc.plp.quickLocationFilter.cookieConsentText`,
    defaultMessage: '',
  });
  const grantConsentBannerButton = useI18n({
    id: `nemo.sc.plp.quickLocationFilter.cookieConsentButton`,
    defaultMessage: '',
  });

  const applyFilters = useApplyFilters();
  const clearAllDealerFilters = useClearAllDealerFilters();

  const localStorage = new ExpiringStorage(localStorageLocationSearchInputKey, 30);
  const inputDataFromLocalStorage = localStorage.readEntry();

  const context = React.useContext(FilterContext) as FilterContextValue;
  const {
    radius,
    setRadius,
    searchedCoords,
    prevSearchedCoords,
    setWholeMarketDealersFetch,
    setSearchedCoords,
    setPrevSearchedCoords,
  } = context;

  const [isClient, setIsClient] = React.useState(false);
  const { needConsent, effectiveConsent, libraryLoaded } = useGoogleMaps();

  const inputRef = useRef<HTMLInputElement>(null);
  const prevStateRadius = useRef<any>(null);
  const changedByLocateMe = useRef<boolean>(false);

  const handleLocateMeClick = () => {
    changedByLocateMe.current = true;

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
    };

    const successCallback = (position: GeolocationPosition) => {
      setAddressFromLatLngGeocoder(position, inputRef, localStorageLocationSearchInputKey);

      context.setSearchedCoords({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      });
    };

    const errorCallback = (error: GeolocationPositionError) => {
      // eslint-disable-next-line no-console
      console.error(error);
    };

    navigator.geolocation.getCurrentPosition(successCallback, errorCallback, options);
  };

  const [twoClickOverlayOpened, setIsTwoClickOverlayOpened] = React.useState<boolean>(false);
  const [focusLayerNode, setFocusLayerNode] = React.useState<Element>();
  const [focusLayer, setFocusLayer] = React.useState<LayerHandleV1<{}>>();
  const focusLayerRef = React.useCallback(
    (node: any) => {
      setFocusLayerNode(node);
    },
    [setFocusLayerNode]
  );

  const openConsentOverlay = () => {
    const layer = layerManager.openFocusLayer(
      () => <div ref={focusLayerRef} />,
      {},
      {
        userCloseable: true,
        size: FocusLayerSizeV2.B,
      }
    );
    setFocusLayer(layer);
    setIsTwoClickOverlayOpened(true);
  };

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (inputRef.current && libraryLoaded && effectiveConsent) {
      initializeAutocomplete(
        country,
        inputRef,
        localStorageLocationSearchInputKey,
        setSearchedCoords
      );

      if (inputDataFromLocalStorage) {
        setWholeMarketDealersFetch(false);
        initializeInitialStateFromLocalStorage(
          inputRef,
          inputDataFromLocalStorage,
          setRadius,
          setSearchedCoords
        );
      } else {
        setWholeMarketDealersFetch(true);
      }
    }
  }, [inputRef.current, effectiveConsent, libraryLoaded]);

  useEffect(() => {
    if (searchedCoords && !radius) {
      setRadius(String(defaultRadius));
    } else if (searchedCoords && radius && inputRef.current) {
      if (!isClient) return;
      if (!radius) setRadius(String(defaultRadius));

      const firstAutocompleteSearch = prevStateRadius.current === null;
      const firedByChangedRadius = prevStateRadius.current !== radius;
      const doubleSearchOfSameLocation = prevSearchedCoords?.latitude === searchedCoords.latitude;

      const geoFilterDTO: FilterDTO = {
        id: 'geo',
        active: true,
        type: 'location-filter',
        values: [
          searchedCoords?.latitude as any,
          searchedCoords?.longitude as any,
          radius,
          mileageUnit,
          inputRef.current.value,
        ],
        preset: enableMandatoryAreaSearch,
      };

      const requestData = async () => {
        // eslint-disable-next-line no-unused-expressions
        shouldBeUsedFilterDataWithoutClearingDealers(firedByChangedRadius, firstAutocompleteSearch)
          ? await applyFilters(geoFilterDTO)
          : await clearAllDealerFilters(geoFilterDTO);
      };

      // eslint-disable-next-line no-console
      if (!doubleSearchOfSameLocation || firedByChangedRadius) requestData().catch(console.error);

      prevStateRadius.current = radius;
      // eslint-disable-next-line no-param-reassign
      changedByLocateMe.current = false;
      setPrevSearchedCoords({
        latitude: searchedCoords?.latitude,
        longitude: searchedCoords?.longitude,
      });
      setWholeMarketDealersFetch(false);
    }
  }, [searchedCoords, radius]);

  useEffect(() => {
    if (radius) {
      updateLocationSearchInputLocalStorage(localStorageLocationSearchInputKey, 'radius', radius);
    }
  }, [radius]);

  useEffect(() => {
    if (inputRef.current?.value && inputRef.current?.value) {
      updateLocationSearchInputLocalStorage(
        localStorageLocationSearchInputKey,
        'inputName',
        inputRef.current.value
      );
    }
  }, [inputRef.current?.value]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = inputDataFromLocalStorage?.inputName || '';
    }
  }, [inputDataFromLocalStorage?.inputName]);

  useEffect(() => {
    if (effectiveConsent) {
      focusLayer?.close();
      setIsTwoClickOverlayOpened(false);
    }
  }, [effectiveConsent]);

  const autocompleteCSS = `
    .pac-container {
      background: ${dynamicStylingHelper('#ffffff', SYS_GRADIENT_ELEVATION_LEVEL3_FILL_DARK)};
      padding-block: ${dynamicStylingHelper('24px 24px 24px 24px', `${SYS_SPACE_FIXED_300} ${SYS_SPACE_FIXED_200} ${SYS_SPACE_FIXED_200} ${SYS_SPACE_FIXED_200}`)};
      padding-inline: 16px 16px;
      z-index: 10000;
      border-radius: ${dynamicStylingHelper('0px', '20px')};
      ${dynamicStyling({
        alpha: `
        border-top: 0px;
      `,
      })};
    }

    .pac-logo:after {
    ${dynamicStyling({
      alpha: `
      right: 0px;
        bottom: 2px;
        position: relative;
    `,
    })};
    }

    @media (max-width: 1023px) {
      .pac-container {
        position: fixed !important;
      }
    }

    .pac-icon {
      display: none;
    }
   .pac-item,.pac-item-query, .pac-matched {
      color: ${dynamicStylingHelper('#333333', '#ccc')};
      font-size: 16px;
      font-family: AudiType,sans-serif;
      font-stretch: 105%;
      line-height: 24px;
      font-weight: 400;
      border-top: none;
    }
    .pac-item{
      padding: ${dynamicStylingHelper('0px', SYS_SPACE_FIXED_150)}; 
    }
     .pac-item:not(:last-child) {
      border-block-end: ${dynamicStylingHelper('1px solid #d9d9d9', `2px solid hsla(240, 20%, 99%, 0.2)`)};
      padding-block-end: ${dynamicStylingHelper('10px', SYS_SPACE_FIXED_150)};
    }
    .pac-item:not(:first-child) {
      padding-block-start: ${dynamicStylingHelper('10px', SYS_SPACE_FIXED_150)};
    }
    .pac-item:hover, .pac-item-selected, .pac-item-selected:hover{
      background-color: ${dynamicStylingHelper('#f2f2f2', SYS_COLOR_SURFACE_NEUTRAL_700_DARK)};
      padding-inline-start: ${dynamicStylingHelper('8px', SYS_SPACE_FIXED_150)};
      cursor: pointer;
    }
    .pac-item:hover span, .pac-item-selected:hover span, .pac-item-selected span{
      ${dynamicStylingHelper('color: #000000')};
    }
    .marker-with-counter{
      margin-top: -6px;
    }
  `;

  return (
    <QuickLocationFilterWrapper>
      <SubHeadText
        tag="p"
        variant={{ type: 'copy', order: dynamicStylingHelper('2', '1'), style: 'short' }}
        margin={[0, 0, SYS_SPACE_FIXED_150, 0]}
      >
        {locationSearchHeadlineLabel}
      </SubHeadText>
      {!effectiveConsent && (
        <StyledInfoBox
          title={{
            tag: 'h2',
            text: grantConsentBannerHeadline,
          }}
          copy={grantConsentBannerText}
          margin={[0, 0, '28px', 0]}
          textLink={{
            label: grantConsentBannerButton,
            onClick: () => {
              openConsentOverlay();
              setIsTwoClickOverlayOpened(true);
              trackGiveConsent(trackingService, grantConsentBannerButton);
            },
          }}
        />
      )}
      <>
        {twoClickOverlayOpened &&
          focusLayerNode &&
          ReactDOM.createPortal(
            <>
              <TwoClickStyle />
              <ConsentModal
                theme="light"
                headerText={consentModalHeadline}
                bodyText={consentModalBody}
                toggleText={consentModalToggle}
                showCloseButton
                showRedirectButton={false}
                closeButtonCallback={() => {
                  focusLayer?.close();
                  setIsTwoClickOverlayOpened(false);
                }}
                twoClickStyle={{
                  position: 'relative',
                  padding: 0,
                  top: 'unset',
                  transform: 'unset',
                  maxHeight: 'unset',
                  width: 'unset',
                  left: 'unset',
                }}
                twoClickCancelCtaStyle={{
                  display: 'none',
                }}
              />
            </>,
            focusLayerNode
          )}
      </>
      <SearchWrapper>
        <style>{autocompleteCSS}</style>
        <StyledSearchField
          inputRef={inputRef}
          additionalIconButton={{
            onClick: () => {
              if (isLegacy && inputRef?.current) {
                inputRef.current.value = '';
              } else {
                handleLocateMeClick();
              }
            },
            icon: dynamicStylingHelper('cancel', 'gps'),
            'aria-label': dynamicStylingHelper(
              clearLocationSearchInputButtonAriaLabel,
              locateMeButtonAriaLabel
            ),
          }}
          value={undefined!}
          id="quickLocationFilterInput"
          label={locationSearchDefaultInputLabel}
          clearButton={{
            'aria-label': clearLocationSearchInputButtonAriaLabel,
            onClick: () => {},
          }}
          disabled={!effectiveConsent}
        />
        <DynamicComponent
          legacy={
            <LocateMeButton
              variant="secondary"
              icon="gps"
              size="small"
              disabled={!effectiveConsent}
              onClick={() => handleLocateMeClick()}
              aria-label={locateMeButtonAriaLabel}
            />
          }
        />
      </SearchWrapper>
      <DynamicComponent
        legacy={
          <Text
            variant={{ type: 'copy', style: 'normal', order: '2' }}
            margin={[0, 0, SYS_SPACE_FIXED_300, 0]}
          >
            {locationSearchInputLabel}
          </Text>
        }
      />
      <SelectField
        id="select_radius"
        label={distanceRadiusLabel}
        onChange={(e: any) => {
          setRadius(e.target.value);
        }}
        value={radius!}
        disabled={!effectiveConsent || !radius}
      >
        {radiusOptions.map((radiusOption, index) => {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <option value={radiusOption} key={index}>
              + {radiusOption} {mileageUnit || 'km'}
            </option>
          );
        })}
      </SelectField>
      {effectiveConsent && needConsent && (
        <ToggleSwitch
          id="quick_location_filter_cookie_consent"
          label={toggleText}
          labelPosition="trailing"
          margin={[SYS_SPACE_FIXED_300, 0, 0, 0]}
          name="toggle-switch"
          onChange={() => {
            // set location filter to default
            setLocationFilterToDefaultState(
              getConfiguration()!,
              getEnvironmentConfig()!,
              clearAllDealerFilters,
              context,
              inputRef,
              enableMandatoryAreaSearch
            );
            // remove consent
            saveConsentToStorage(false);
            // tracking event
            trackRemoveConsent(trackingService);
            // open MAS overlay
            if (enableMandatoryAreaSearch) {
              openMandatoryAreaSearchModal();
            }
          }}
          value="toggle-switch"
          checked={effectiveConsent}
        />
      )}
    </QuickLocationFilterWrapper>
  );
};

export const StyledSearchField = styled((props) => <SearchField {...props} />)<SearchFieldProps>`
  min-width: unset;
`;

export const StyledInfoBox = styled((props) => <InfoBox {...props} />)<InfoBoxProps>``;

const TwoClickStyle = createGlobalStyle`
  .two-click-alpha {
    box-shadow: none!important;
    a {
      color: ${dynamicStylingHelper('#333333', SYS_COLOR_TEXT_COPY_DEFAULT_DARK)};
    }
  }
`;
